import {
  CLIENT_ERROR_MESSAGE_MAX,
  CLIENT_ERROR_STACK_MAX,
  type ClientErrorKind,
  type ClientErrorReport,
  ORG_ID_HEADER,
} from '@clinic/contracts';
import { sessionStore } from '../auth/session-store';

/**
 * Failures in the page reach the server log (docs/debugging/design.md): an uncaught exception, an
 * unhandled rejection that is not an API or network failure (the server logs those itself), a part
 * of the app that did not load, and an API answer that breaks the shared contract. The report has
 * the error's message and stack, the page's path without its query, and the build; never text from
 * the page. The server labels it with the signed-in user and organization.
 */

/** The web build: the commit (set by the deploy as VITE_BUILD_ID), or "dev". */
export const RELEASE = (import.meta.env.VITE_BUILD_ID as string | undefined)?.slice(0, 64) || 'dev';

/** At most this many reports from one page load, and one per distinct failure. */
const MAX_REPORTS = 20;
const ORG_IN_PATH = /^\/o\/([0-9a-f-]{36})(?:\/|$)/i;

const sent = new Set<string>();
let count = 0;

/** Ignored: a cancelled request, and an API or network failure (the server logs its own side). */
function ignored(error: unknown): boolean {
  // DOMException is not an Error in every engine.
  const name = typeof error === 'object' && error !== null && 'name' in error ? error.name : undefined;
  return name === 'AbortError' || name === 'ApiError' || name === 'NetworkError';
}

/** A failed lazy import of a part of the app (Chrome, Firefox and Safari word it differently). */
export function isChunkError(message: string): boolean {
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|Unable to preload CSS/i.test(
    message,
  );
}

function clip(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) : s;
}

/** The report for an error, or null when it is not worth one. */
export function toReport(
  kind: ClientErrorKind,
  error: unknown,
  extra: { traceId?: string | null; path?: string } = {},
): ClientErrorReport | null {
  if (ignored(error)) return null;
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const stack = error instanceof Error ? error.stack : undefined;
  const report: ClientErrorReport = {
    kind: kind === 'error' || kind === 'rejection' ? (isChunkError(message) ? 'chunk' : kind) : kind,
    message: clip(message, CLIENT_ERROR_MESSAGE_MAX),
    path: clip(extra.path ?? window.location.pathname, 300),
    release: RELEASE,
  };
  if (stack) report.stack = clip(stack, CLIENT_ERROR_STACK_MAX);
  if (extra.traceId) report.traceId = extra.traceId;
  return report;
}

/** Sends one report. Never throws and never retries: a report is help, not work. */
export function reportClientError(
  kind: ClientErrorKind,
  error: unknown,
  extra: { traceId?: string | null; path?: string } = {},
): void {
  try {
    const report = toReport(kind, error, extra);
    if (!report) return;
    const key = `${report.kind}|${report.message}|${report.path}`;
    if (sent.has(key) || count >= MAX_REPORTS) return;
    sent.add(key);
    count += 1;
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = sessionStore.get()?.accessToken;
    if (token) headers.authorization = `Bearer ${token}`;
    const org = ORG_IN_PATH.exec(window.location.pathname)?.[1];
    if (org) headers[ORG_ID_HEADER] = org;
    void fetch('/v1/client-errors', {
      method: 'POST',
      headers,
      body: JSON.stringify(report),
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Reporting must never be the next failure.
  }
}

/** Listens for uncaught failures for the life of the page. */
export function startErrorReporting(): void {
  window.addEventListener('error', (e) => {
    // Only the app's own scripts: an extension or another site's script says nothing about ours.
    if (e.filename && !e.filename.startsWith(window.location.origin)) return;
    reportClientError('error', e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => reportClientError('rejection', e.reason));
}

/** For the tests: forget what was sent. */
export function resetErrorReporting(): void {
  sent.clear();
  count = 0;
}
